/**
 * Payment Method Selector Component
 * UI component for selecting payment methods with amount input
 */

import React, { useState, useEffect } from 'react';
import { Button } from './ui';

export interface PaymentMethod {
  id: string;
  name: string;
  type: 'UPI' | 'CARD' | 'NET_BANKING' | 'BANK_TRANSFER';
  enabled: boolean;
  minAmount: number;
  maxAmount: number;
  processingFee: number;
  estimatedTime: string;
  supportedBanks?: string[];
}

interface PaymentMethodSelectorProps {
  methods: PaymentMethod[];
  selectedMethod: PaymentMethod | null;
  amount: number;
  onMethodSelect: (method: PaymentMethod) => void;
  onAmountChange: (amount: number) => void;
  onProceed: () => void;
  loading?: boolean;
  className?: string;
}

export const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  methods,
  selectedMethod,
  amount,
  onMethodSelect,
  onAmountChange,
  onProceed,
  loading = false,
  className = ''
}) => {
  const [amountError, setAmountError] = useState<string | null>(null);
  const [formattedAmount, setFormattedAmount] = useState<string>('');

  useEffect(() => {
    if (amount > 0) {
      setFormattedAmount(amount.toString());
    }
  }, [amount]);

  const validateAmount = (value: number, method: PaymentMethod | null): string | null => {
    if (!value || value <= 0) {
      return 'Please enter a valid amount';
    }

    if (method) {
      const minAmount = method.minAmount / 100; // Convert from paise to rupees
      const maxAmount = method.maxAmount / 100;

      if (value < minAmount) {
        return `Minimum amount for ${method.name} is ₹${minAmount}`;
      }

      if (value > maxAmount) {
        return `Maximum amount for ${method.name} is ₹${maxAmount.toLocaleString()}`;
      }
    }

    return null;
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setFormattedAmount(value);
    
    const numericValue = parseFloat(value);
    if (!isNaN(numericValue)) {
      onAmountChange(numericValue);
      const error = validateAmount(numericValue, selectedMethod);
      setAmountError(error);
    } else {
      setAmountError('Please enter a valid amount');
    }
  };

  const handleMethodSelect = (method: PaymentMethod) => {
    onMethodSelect(method);
    
    // Re-validate amount with new method
    if (amount > 0) {
      const error = validateAmount(amount, method);
      setAmountError(error);
    }
  };

  const getMethodIcon = (type: string) => {
    switch (type) {
      case 'UPI':
        return '📱';
      case 'CARD':
        return '💳';
      case 'NET_BANKING':
        return '🏦';
      case 'BANK_TRANSFER':
        return '💰';
      default:
        return '💳';
    }
  };

  const getMethodDescription = (method: PaymentMethod) => {
    switch (method.type) {
      case 'UPI':
        return 'Pay using UPI apps like PhonePe, Google Pay, Paytm';
      case 'CARD':
        return 'Credit/Debit cards with 3D Secure authentication';
      case 'NET_BANKING':
        return 'Direct bank account transfer via net banking';
      case 'BANK_TRANSFER':
        return 'Manual bank transfer (NEFT/RTGS/IMPS)';
      default:
        return '';
    }
  };

  const canProceed = selectedMethod && amount > 0 && !amountError && !loading;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Amount Input */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Enter Amount</h3>
        
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-500 text-lg">₹</span>
          </div>
          <input
            type="text"
            value={formattedAmount}
            onChange={handleAmountChange}
            placeholder="0.00"
            className={`block w-full pl-8 pr-3 py-3 text-lg border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white ${
              amountError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
            }`}
            style={{ color: '#111827' }}
          />
        </div>
        
        {amountError && (
          <p className="mt-2 text-sm text-red-600">{amountError}</p>
        )}
        
        {amount > 0 && !amountError && (
          <p className="mt-2 text-sm text-gray-600">
            Amount: ₹{amount.toLocaleString()} + GST (18%) = ₹{(amount * 1.18).toLocaleString()}
          </p>
        )}
      </div>

      {/* Payment Methods */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Payment Method</h3>
        
        <div className="space-y-3">
          {methods.map((method) => {
            const isSelected = selectedMethod?.id === method.id;
            const isDisabled = !method.enabled || loading;
            
            return (
              <button
                key={method.id}
                onClick={() => !isDisabled && handleMethodSelect(method)}
                disabled={isDisabled}
                className={`w-full p-4 text-left border rounded-lg transition-colors ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : isDisabled
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{getMethodIcon(method.type)}</span>
                    <div>
                      <div className="font-medium text-gray-900">{method.name}</div>
                      <div className="text-sm text-gray-500">{getMethodDescription(method)}</div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm text-gray-600">{method.estimatedTime}</div>
                    {method.processingFee > 0 && (
                      <div className="text-xs text-gray-500">
                        Fee: ₹{(method.processingFee / 100).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
                
                {isSelected && (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <div className="text-sm text-blue-700">
                      <div>Min: ₹{(method.minAmount / 100).toLocaleString()}</div>
                      <div>Max: ₹{(method.maxAmount / 100).toLocaleString()}</div>
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        
        {methods.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No payment methods available
          </div>
        )}
      </div>

      {/* Proceed Button */}
      <div className="flex justify-end">
        <Button
          onClick={onProceed}
          disabled={!canProceed}
          isLoading={loading}
          className="px-8 py-3 text-lg"
        >
          {loading ? 'Processing...' : 'Proceed to Payment'}
        </Button>
      </div>

      {/* Security Notice */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-gray-900">Secure Payment</h4>
            <p className="text-sm text-gray-600 mt-1">
              Your payment information is encrypted and secure. We use industry-standard security measures to protect your data.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodSelector;